import React, { useMemo, useCallback } from 'react';
import type { DateGroupType, MessageType } from '../hooks/useConversations';
import { PieChart, Pie, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, Cell } from 'recharts';
import '../styles/Main.css';
import DateGroup from './DateGroup';

interface MainProps {
  selected: string;
  conversations: DateGroupType[];
  loadingConversation: boolean;
  currentLang: 'es' | 'en';
  onLangChange: (lang: 'es' | 'en') => void;
  isAdmin?: boolean;
  onControl?: () => void;
  onSendMessage?: (userId: string, message: string, date: string) => void;
}

const Main: React.FC<MainProps> = ({
  selected,
  conversations,
  loadingConversation,
  currentLang,
  onLangChange,
  isAdmin = false,
  onControl,
  onSendMessage,
}) => {
  const [activeTab, setActiveTab] = React.useState<'conversations' | 'stats'>('conversations');
  const [expandAll, setExpandAll] = React.useState(false);
  const [convSubTab, setConvSubTab] = React.useState<'participantes' | 'educators'>('participantes');

  const t = useCallback((es: string, en: string) => (currentLang === 'es' ? es : en), [currentLang]);
  // Variables y estados para envío masivo de mensaje a todos
  const [broadcasting, setBroadcasting] = React.useState(false);
  const [broadcastStatus, setBroadcastStatus] = React.useState<'success' | 'error' | null>(null);
  const [showBroadcastModal, setShowBroadcastModal] = React.useState(false);
  const [broadcastInput, setBroadcastInput] = React.useState('');
  const allUserIds = useMemo(() => Array.from(new Set(conversations.flatMap(g => g.messages.map(m => m.userId)))), [conversations]);
  const phoneNumbers = useMemo(() => allUserIds.map(id => id.split('|')[0].trim()), [allUserIds]);
  // URL fija para envío masivo a todos los usuarios con code
  const BROADCAST_URL = 'https://headspringchatbot.azurewebsites.net/api/enviar_anuncio?code=JKH6GMS7gyHQJZiYXONirlDB_C86jgIi4yHQsgF0OhZpAzFutEXZKw==';
  
  // Función que envía el mensaje desde el modal
  const handleBroadcast = async () => {
    if (!broadcastInput.trim()) return;
    setBroadcasting(true);
    setBroadcastStatus(null);
    try {
      const res = await fetch(BROADCAST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuarios: phoneNumbers, mensaje: broadcastInput }),
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setBroadcastStatus(json.result ? 'success' : 'error');
      if (json.result) {
        setTimeout(() => setShowBroadcastModal(false), 1000);
      }
    } catch {
      setBroadcastStatus('error');
    } finally {
      setBroadcasting(false);
    }
  };

  const stats = useMemo(() => {
    const flatMessages = conversations.flatMap(g => g.messages as (MessageType & { categoria?: string; idioma_respuesta?: string })[]);
    
    if (flatMessages.length === 0) {
      return {
        totalMessages: 0,
        uniqueUsers: 0,
        categoryData: [],
        languageData: [],
      };
    }

    const uniqueUserIds = new Set(flatMessages.map(m => m.userId));

    const categoryCounts = flatMessages.reduce((acc: Record<string, number>, msg) => {
      const cat = msg.categoria || t('Sin categoría', 'Uncategorized');
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {});
    const allCategoryData = Object.entries(categoryCounts).map(([name, value]) => ({ name, value }));
    const filteredCategoryData = allCategoryData.filter(item => item.name !== t('Sin categoría', 'Uncategorized'));
    const categoryData = filteredCategoryData.length > 0 ? filteredCategoryData : allCategoryData;

    const langCounts = flatMessages.reduce((acc: Record<string, number>, msg) => {
      const lang = msg.idioma_respuesta || t('Desconocido', 'Unknown');
      acc[lang] = (acc[lang] || 0) + 1;
      return acc;
    }, {});
    const allLanguageData = Object.entries(langCounts).map(([name, value]) => ({ name, value }));
    const filteredLanguageData = allLanguageData.filter(item => item.name !== t('Desconocido', 'Unknown'));
    const languageData = filteredLanguageData.length > 0 ? filteredLanguageData : allLanguageData;

    return {
      totalMessages: flatMessages.length,
      uniqueUsers: uniqueUserIds.size,
      categoryData,
      languageData,
    };
  }, [conversations, t]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF1943'];

  return (
    <main className="main">
      <div className="main-header">
        <h1>
          {selected ? `${t('Programa', 'Program')}: ${selected}` : t('Selecciona un programa', 'Select a program')}
        </h1>
        <div className="header-actions">
          {activeTab === 'conversations' && (
            <button className="expand-all-btn" onClick={() => setExpandAll(prev => !prev)}>
              {expandAll ? t('Contraer todo', 'Collapse All') : t('Desplegar', 'Show All')}
            </button>
          )}
          {activeTab === 'conversations' && !loadingConversation && conversations.length > 0 && (
            <button className="broadcast-btn" onClick={() => setShowBroadcastModal(true)}>
              {t('Enviar a todos', 'Send to All')}
            </button>
          )}
          {broadcastStatus === 'success' && <span className="broadcast-success">{t('Envío exitoso', 'Send successful')}</span>}
          {broadcastStatus === 'error' && <span className="broadcast-error">{t('Error de envío', 'Sending error')}</span>}
          {isAdmin && onControl && (
            <button className="control-btn" onClick={onControl}>
              Control
            </button>
          )}
        </div>
      </div>
      {/* Modal de envío masivo */}
      {showBroadcastModal && (
        <div className="broadcast-modal-overlay">
          <div className="broadcast-modal">
            <h3>{t('Enviar mensaje a todos', 'Send message to all')}</h3>
            <textarea
              placeholder={t('Escribe tu mensaje aquí...', 'Type your message here...')}
              value={broadcastInput}
              onChange={e => setBroadcastInput(e.target.value)}
            />
            <div className="modal-btn-group">
              <button className="broadcast-btn" onClick={handleBroadcast} disabled={broadcasting}>
                {broadcasting ? t('Enviando…', 'Sending…') : t('Enviar', 'Send')}
              </button>
              <button className="cancel-btn" onClick={() => setShowBroadcastModal(false)} disabled={broadcasting}>
                {t('Cancelar', 'Cancel')}
              </button>
            </div>
            {broadcastStatus === 'success' && <p className="broadcast-success">{t('Envío exitoso', 'Send successful')}</p>}
            {broadcastStatus === 'error' && <p className="broadcast-error">{t('Error de envío', 'Sending error')}</p>}
          </div>
        </div>
      )}

      <div className="tabs">
        <button
          className={activeTab === 'conversations' ? 'active' : ''}
          onClick={() => setActiveTab('conversations')}
        >
          {t('Conversaciones', 'Conversations')}
        </button>
        <button
          className={activeTab === 'stats' ? 'active' : ''}
          onClick={() => setActiveTab('stats')}
        >
          {t('Estadísticas', 'Statistics')}
        </button>
      </div>

      {activeTab === 'conversations' && (
        <div className="tab-content">
          <div className="sub-tabs">
            <button
              className={convSubTab === 'participantes' ? 'active' : ''}
              onClick={() => setConvSubTab('participantes')}
            >
              {t('Participantes', 'Participants')}
            </button>
            <button
              className={convSubTab === 'educators' ? 'active' : ''}
              onClick={() => setConvSubTab('educators')}
            >
              {t('Educadores', 'Educators')}
            </button>
          </div>
          {/* Botón de envío masivo movido al header */}
          {loadingConversation ? (
            <p>{t('Cargando conversación…', 'Loading conversation…')}</p>
          ) : conversations.length === 0 ? (
            <p>{t('No hay mensajes para este programa.', 'No messages for this program.')}</p>
          ) : (
            conversations
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .map(group => ({
                date: group.date,
                messages: group.messages.filter(m =>
                  convSubTab === 'participantes'
                    ? m.tipoUsuario !== 'Profesor'
                    : m.tipoUsuario === 'Profesor'
                ),
              }))
              .filter(group => group.messages.length > 0)
              .map(group => (
                <DateGroup
                  key={group.date}
                  date={group.date}
                  messages={group.messages}
                  currentLang={currentLang}
                  onSendMessage={onSendMessage}
                  expandAll={expandAll}
                  assistantId={selected}
                />
              ))
          )}
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="tab-content">
          <section className="stats-section">
            <h3>{t('Métricas Clave', 'Key Metrics')}</h3>
            <div className="key-metrics">
              <div className="metric-card">
                <h4>{t('Usuarios Únicos', 'Unique Users')}</h4>
                <p>{stats.uniqueUsers}</p>
              </div>
              <div className="metric-card">
                <h4>{t('Total Mensajes', 'Total Messages')}</h4>
                <p>{stats.totalMessages}</p>
              </div>
            </div>

            <div className="chart-row">
              <div className="chart-container">
                <h4>{t('Distribución por Categoría', 'Category Distribution')}</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={stats.categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                      {stats.categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-container">
                <h4>{t('Distribución por Idioma', 'Language Distribution')}</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.languageData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>
        </div>
      )}
    </main>
  );
};

export default Main; 